// src/pages/projects/ManageProjectsPage.jsx
// Extracted from App.js - Manage Projects page
// BRIO Migration: Table default code "1a", Edit modal table code "1b"
// Edit Project modal redesigned as compact form-as-table layout
import React, { useState } from 'react';
import { Plus, Briefcase, MapPin } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioActionGroup,
  BrioColorIndicator,
  BrioFrame,
  BrioModal,
  getTablePropsFromCode
} from '../../components/BrioComponents';


// Presentation codes
const PRESENTATION_CODE = '1a';
const MODAL_TABLE_CODE = '1b';

// ── Shared input style ──────────────────────────────────────────────
/* @audit cat="05" issue="Inline INPUT_STYLE with borderRadius 6px" fix="Use BrioInput component or .brio-modal-input class" */
const INPUT_STYLE = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--brio-border, #d1d5db)',
  borderRadius: '6px',
  fontSize: '13px',
  lineHeight: '1.4',
  background: '#fff',
  color: 'var(--brio-text, #1e293b)',
  boxSizing: 'border-box',
};

const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };

const HINT_STYLE = {
  fontSize: '11px',
  color: 'var(--brio-muted, #94a3b8)',
  marginTop: '2px',
  lineHeight: '1.3',
};

// ── Reusable table-row component ────────────────────────────────────
function FormRow({ label, hint, children, sectionHeader }) {
  if (sectionHeader) {
    return (
      <tr>
        <td colSpan="2" style={{
          padding: '10px 12px 6px',
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--brio-gold, #9F902A)',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: 'var(--brio-bg-subtle, #fafaf6)',
        }}>
          {label}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={{
        padding: '8px 12px',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
        fontWeight: 500,
        fontSize: '13px',
        color: 'var(--brio-text, #475569)',
        width: '160px',
        minWidth: '130px',
      }}>
        {label}
        {hint && <div style={HINT_STYLE}>{hint}</div>}
      </td>
      <td style={{
        padding: '6px 12px',
        verticalAlign: 'top',
      }}>
        {children}
      </td>
    </tr>
  );
}


export default function ManageProjectsPage({
  // Data
  activeClients,
  activeUsers,
  activeProjects,
  currentUser,
  isAdmin,
  BRIO_PROJECT_TYPES,
  // Functions
  getSortedProjects,
  getProjectStatusStyle,
  suggestProjectTypeFromName,
  autoPickProjectColor,
  // State
  modal,
  form,
  setModal,
  setForm,
  // Persistence
  useSupabaseDB,
  supabaseProjects,
  projects,
  setProjects,
  // Formatters
  fmtCurr,
  fmtProject,
  currSymbol = '₹',
  // Fee revision hook — records fee changes for invoice transparency
  feeRevisionsHook,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const modalTableProps = getTablePropsFromCode(MODAL_TABLE_CODE);

  // Deep-cloned snapshot of the project at time of opening Edit modal.
  // Used to compute oldTotalFee for fee revision detection (avoids
  // reading from the potentially-mutated activeProjects array).
  const [originalProjectSnapshot, setOriginalProjectSnapshot] = useState(null);

  // Bulk-import misc fee scopes (paste field, like office overheads)
  const [showMiscImport, setShowMiscImport] = useState(false);
  const [miscImportText, setMiscImportText] = useState('');

  const handleEditProject = (p) => {
    // Snapshot the project BEFORE any form edits so we can detect fee changes later
    setOriginalProjectSnapshot(JSON.parse(JSON.stringify(p)));

    const retainerConfig = p.retainerConfig || {
      monthlyAmount: 0,
      startDate: null,
      totalMonths: 12,
      status: 'inactive'
    };
    const existingCategory = p.category || '';
    const suggestedCategory = existingCategory || suggestProjectTypeFromName(p.name);
    const initialColor = p.color || (suggestedCategory ? autoPickProjectColor({ type: suggestedCategory, projectId: p.id, projectName: p.name }) : '#3b82f6');
    setForm({
      editProjectId: p.id,
      projectName: p.name,
      projectCode: p.projectCode || '',
      projectAddress: p.projectAddress || '',
      city: p.city || '',
      country: p.country || '',
      clientId: p.clientId,
      category: suggestedCategory,
      status: p.status || 'active',
      color: initialColor,
      budget: p.budget,
      projectSize: p.projectSize || 0,
      plotArea: p.plotArea ?? null,
      targetConstructionBudget: p.targetConstructionBudget || 0,
      finalConstructionBudget: p.finalConstructionBudget || 0,
      supervisors: p.supervisors || [],
      budgetAllocation: p.budgetAllocation || {},
      retainerMonthlyAmount: retainerConfig.monthlyAmount || 0,
      retainerStartDate: retainerConfig.startDate || '',
      retainerTotalMonths: retainerConfig.totalMonths || 12,
      retainerStatus: retainerConfig.status || 'inactive',
      autoColorEnabled: true,
      invoiceProjectName: p.invoiceProjectName || '',
      miscFeeScopes: Array.isArray(p.miscFeeScopes) ? p.miscFeeScopes : [],
      contactPreferences: p.contactPreferences || {},
      contractCurrency: p.contractCurrency || 'INR',
      projectEmailId: p.projectEmailId || (p.projectCode ? `${p.projectCode}@architecturebrio.com` : ''),
      requiresProformaInvoice: p.requiresProformaInvoice !== false,
      plannedDateTriggerThresholdDays: p.plannedDateTriggerThresholdDays ?? '',
      commsEnabled: p.commsEnabled ?? false,
      contractingEntity: p.contractingEntity || 'SBPL',
    });
    setModal('editProject');
  };

  const calculateTotalFee = (p) => {
    const baseFee = p.budget || 0;
    const retainerConfig = p.retainerConfig || { monthlyAmount: 0, totalMonths: 12, status: 'inactive' };
    const ccRetainer = retainerConfig.status === 'active'
      ? (retainerConfig.monthlyAmount || 0) * (retainerConfig.totalMonths || 12)
      : 0;
    const miscFee = (Array.isArray(p.miscFeeScopes) ? p.miscFeeScopes : [])
      .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    return baseFee + ccRetainer + miscFee;
  };

  // ── Save handler ──────────────────────────────────────────────────
  const handleSaveProject = async () => {
    // 1️⃣ Entry
    console.log('[FeeRevision] handleSaveProject CALLED');

    if (!form.projectName) {
      setForm({ errorMessage: 'Enter project name' });
      setModal('importError');
      return;
    }

    const currentProject = activeProjects.find(p => p.id === form.editProjectId);
    const budgetAllocation = form.budgetAllocation ?? currentProject?.budgetAllocation ?? {};
    const totalAllocation = Object.values(budgetAllocation).reduce((sum, val) => sum + (val ?? 0), 0);
    if (totalAllocation > 0 && Math.abs(totalAllocation - 100) > 0.01) {
      setForm({ errorMessage: `Budget allocation must equal 100% (currently ${totalAllocation.toFixed(1)}%)` });
      setModal('importError');
      return;
    }

    const updatedProjectData = {
      name: form.projectName,
      projectCode: form.projectCode || '',
      projectAddress: form.projectAddress || '',
      city: form.city || '',
      country: form.country || '',
      clientId: parseInt(form.clientId),
      category: form.category || '',
      status: form.status || 'active',
      color: form.color,
      budget: parseFloat(form.budget) || 0,
      projectSize: parseFloat(form.projectSize) || 0,
      plotArea: form.plotArea != null && form.plotArea !== '' ? parseFloat(form.plotArea) : null,
      targetConstructionBudget: parseFloat(form.targetConstructionBudget) || 0,
      finalConstructionBudget: parseFloat(form.finalConstructionBudget) || 0,
      supervisors: form.supervisors || [],
      budgetAllocation: budgetAllocation,
      retainerConfig: {
        monthlyAmount: parseFloat(form.retainerMonthlyAmount) || 0,
        startDate: form.retainerStartDate || null,
        totalMonths: parseInt(form.retainerTotalMonths) || 12,
        status: form.retainerStatus || 'inactive',
        paidMonths: parseInt(form.retainerPaidMonths) || 0
      },
      invoiceProjectName: (form.invoiceProjectName || '').trim(),
      miscFeeScopes: (form.miscFeeScopes || []).filter(s => s.name && s.name.trim()),
      contactPreferences: form.contactPreferences || {},
      contractCurrency: form.contractCurrency || 'INR',
      projectEmailId: (form.projectEmailId || '').trim(),
      requiresProformaInvoice: form.requiresProformaInvoice !== false,
      plannedDateTriggerThresholdDays: form.plannedDateTriggerThresholdDays !== '' ? parseInt(form.plannedDateTriggerThresholdDays) || null : null,
      commsEnabled: form.commsEnabled ?? false,
      // Admins only: nobody else sees the field, so their save leaves it as stored.
      ...(isAdmin ? { contractingEntity: form.contractingEntity || 'SBPL' } : {}),
    };

    if (useSupabaseDB) {
      // 2️⃣ Context check
      console.log('[FeeRevision] context', {
        projectId: form.editProjectId,
        role: currentUser?.role,
        hasFeeRevisionsHook: !!feeRevisionsHook,
        hasCreateRevision: !!feeRevisionsHook?.createRevision,
        hasSnapshot: !!originalProjectSnapshot,
        snapshotBudget: originalProjectSnapshot?.budget,
        snapshotRetainer: originalProjectSnapshot?.retainerConfig,
      });

      // ── Compute old total fee from the pre-edit snapshot ─────────────
      const oldTotalFee = originalProjectSnapshot
        ? calculateTotalFee(originalProjectSnapshot)
        : 0;

      const result = await supabaseProjects.updateProject(form.editProjectId, updatedProjectData);
      if (!result.success) {
        alert('Error updating project: ' + result.error);
        return;
      }

      // Auto-create Dropbox press folders when comms is newly enabled
      const wasCommsEnabled = currentProject?.commsEnabled ?? false;
      if (updatedProjectData.commsEnabled && !wasCommsEnabled && updatedProjectData.projectCode && updatedProjectData.name) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session) return;
          fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-press-folders`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ projects: [{ project_code: updatedProjectData.projectCode, name: updatedProjectData.name }] }),
          }).catch(() => {});
        });
      }

      // ── Compute new total fee from the form data being saved ─────────
      const newTotalFee = calculateTotalFee(updatedProjectData);

      // 3️⃣ Fee comparison
      console.log('[FeeRevision] fee comparison', {
        oldTotalFee,
        newTotalFee,
        delta: newTotalFee - oldTotalFee,
        feesEqual: oldTotalFee === newTotalFee,
        oldBudget: originalProjectSnapshot?.budget,
        newBudget: updatedProjectData?.budget,
        oldRetainerStatus: originalProjectSnapshot?.retainerConfig?.status,
        newRetainerStatus: updatedProjectData?.retainerConfig?.status,
      });

      // ── Create fee revision if fee changed ──
      if (oldTotalFee !== newTotalFee) {
        if (!feeRevisionsHook?.createRevision) {
          console.error('[FeeRevision] SKIPPED — feeRevisionsHook.createRevision not available', {
            hookExists: !!feeRevisionsHook,
            methods: feeRevisionsHook ? Object.keys(feeRevisionsHook) : 'N/A',
          });
        } else {
          const canCreateRevision =
            currentUser.role === 'admin' ||
            currentUser.role === 'admin-pa' ||
            currentUser.role === 'supervisor';

          if (!canCreateRevision) {
            console.log('[FeeRevision] SKIPPED — role guard blocked', { role: currentUser?.role });
          } else {
            // 4️⃣ About to insert
            console.log('[FeeRevision] WILL INSERT fee revision', {
              projectId: form.editProjectId,
              oldTotalFee,
              newTotalFee,
              delta: newTotalFee - oldTotalFee,
            });

            try {
              const revResult = await feeRevisionsHook.createRevision({
                projectId: form.editProjectId,
                effectiveDate: new Date().toISOString().slice(0, 10),
                oldTotalFee,
                newTotalFee,
                reason: 'Fee updated in project settings',
                currency: form.contractCurrency || 'INR',
              });
              if (revResult.success) {
                // 5️⃣ Success
                console.log('[FeeRevision] INSERT SUCCESS', revResult.data);
              } else {
                // 6️⃣ Failure (returned error, not thrown)
                console.error('[FeeRevision] INSERT FAILED', revResult.error);
              }
            } catch (err) {
              // 6️⃣ Failure (thrown)
              console.error('[FeeRevision] INSERT FAILED (exception)', err);
            }
          }
        }
      } else {
        console.log('[FeeRevision] SKIPPED — fees identical, no revision needed');
      }
    } else {
      setProjects(projects.map(p => p.id === form.editProjectId ? { ...p, ...updatedProjectData } : p));
    }
    setModal(null);
    setForm({});
    setOriginalProjectSnapshot(null);  // Clear pre-edit snapshot
  };

  // ── Add Project handler (moved from App.js) ──────────────────────────
  const handleAddProject = async () => {
    if (!form.projectName) { alert('Enter project name'); return; }

    const suggestedCategory = form.category || suggestProjectTypeFromName(form.projectName);
    const autoColor = suggestedCategory
      ? autoPickProjectColor({ type: suggestedCategory, projectId: Date.now(), projectName: form.projectName })
      : (form.color || '#3b82f6');

    const projectData = {
      id: Date.now(),
      name: form.projectName,
      projectCode: form.projectCode || '',
      projectAddress: form.projectAddress || '',
      city: form.city || '',
      country: form.country || '',
      clientId: parseInt(form.clientId) || activeClients[0]?.id,
      category: form.category || '',
      status: 'active',
      color: form.color || autoColor,
      budget: parseFloat(form.budget) || 0,
      projectSize: parseFloat(form.projectSize) || 0,
      plotArea: form.plotArea != null && form.plotArea !== '' ? parseFloat(form.plotArea) : null,
      targetConstructionBudget: parseFloat(form.targetConstructionBudget) || 0,
      finalConstructionBudget: parseFloat(form.finalConstructionBudget) || 0,
      supervisors: [],
      budgetAllocation: {},
      stageTimeline: {},
      retainerConfig: {
        monthlyAmount: 0,
        startDate: null,
        totalMonths: 12,
        status: 'inactive',
      },
      contractCurrency: form.contractCurrency || 'INR',
    };

    if (useSupabaseDB) {
      const result = await supabaseProjects.createProject(projectData);
      if (!result.success) {
        alert('Error creating project: ' + result.error);
        return;
      }
    } else {
      setProjects([...projects, projectData]);
    }
    setModal(null);
    setForm({});
  };

  // ── Computed values for the fee summary ───────────────────────────
  const baseFee = parseFloat(form.budget) || 0;
  const retainerMonthly = parseFloat(form.retainerMonthlyAmount) || 0;
  const retainerMonths = parseFloat(form.retainerTotalMonths) || 12;
  const retainerActive = form.retainerStatus === 'active';
  const ccRetainer = retainerActive ? retainerMonthly * retainerMonths : 0;
  const miscFeeTotal = (form.miscFeeScopes || []).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const totalDesignFee = baseFee + ccRetainer + miscFeeTotal;

  // Construction budget variance
  const targetCB = parseFloat(form.targetConstructionBudget) || 0;
  const finalCB = parseFloat(form.finalConstructionBudget) || 0;
  const cbVariance = finalCB - targetCB;
  const cbPercent = targetCB > 0 ? ((cbVariance / targetCB) * 100).toFixed(1) : 0;

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

            </div>
            {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
              <div className="brio-page-header__right brio-flex brio-items-center brio-gap-xs">
                <button
                  onClick={() => setModal('addProject')}
                  className="brio-btn brio-btn-primary"
                >
                  <Plus className="w-4 h-4"/>Add Project
                </button>
              </div>
            )}
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {getSortedProjects().length > 0 ? (
            <BrioAnalyticsSurface padding="m">
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th>project</th>
                      <th>status</th>
                      {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
                        <>
                          <th className="text-right">total design fee</th>
                          <th className="text-right">size</th>
                          <th className="text-center col-actions">actions</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedProjects().map(p => {
                      const c = activeClients.find(x => x.id === p.clientId);
                      const statusStyle = getProjectStatusStyle(p.status);
                      return (
                        <tr key={p.id} className="brio-row--hoverable">
                          <td>
                            <div className="brio-flex brio-items-center brio-gap-xs">
                              <BrioColorIndicator color={p.color} />
                              <div>
                                <div className="brio-font-normal">{fmtProject(p)}</div>
                                <div className="brio-text-s brio-text-grey brio-italic">{c?.organisation || c?.name}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`brio-badge ${
                              p.status === 'active' ? 'brio-badge--approved' :
                              p.status === 'completed' ? 'brio-badge--pending' :
                              'brio-badge--warning'
                            }`}>
                              {statusStyle.label}
                            </span>
                          </td>
                          {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
                            <>
                              <td className="text-right">
                                <span className="brio-text-grey">{fmtCurr(calculateTotalFee(p), p.contractCurrency)}</span>
                              </td>
                              <td className="text-right">
                                <span className="brio-text-grey">
                                  {p.projectSize ? `${p.projectSize.toLocaleString()} m²` : 'N/A'}
                                </span>
                              </td>
                              <td className="text-center">
                                <BrioActionGroup center>
                                  <BrioIconButton
                                    icon="edit"
                                    variant="edit"
                                    title="Edit project"
                                    onClick={() => handleEditProject(p)}
                                  />
                                </BrioActionGroup>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </BrioAnalyticsSurface>
          ) : (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                No projects added yet
              </div>
            </BrioAnalyticsSurface>
          )}

          {currentUser.role !== 'admin' && (
            <p className="brio-analytics-muted brio-mt-s">All users can add time to any project. Contact admin to create new projects.</p>
          )}
        </div>
      </BrioFrame>

      {/* ════════════════════════════════════════════════════════════
          ADD PROJECT MODAL — BRIO table 1b form layout
          (moved from App.js, restyled to match Edit modal)
         ════════════════════════════════════════════════════════════ */}
      {modal === 'addProject' && (
        <BrioModal
          title="Add Project"
          onClose={() => { setModal(null); setForm({}); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button
                onClick={() => { setModal(null); setForm({}); }}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleAddProject} className="brio-btn brio-btn-primary">
                Add Project
              </button>
            </BrioActionGroup>
          }
        >
          <BrioByCode code={MODAL_TABLE_CODE}>
            <BrioTable {...modalTableProps}>
              <tbody>

                {/* ── Identity ──────────────────────────────────── */}
                <FormRow sectionHeader label="Identity" />

                <FormRow label="Name *">
                  <input
                    type="text"
                    placeholder="Project name"
                    value={form.projectName || ''}
                    onChange={e => setForm({ ...form, projectName: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Code" hint="Max 4 chars">
                  <input
                    type="text"
                    placeholder="ACME"
                    maxLength="4"
                    value={form.projectCode || ''}
                    onChange={e => setForm({ ...form, projectCode: e.target.value.toUpperCase() })}
                    style={{ ...INPUT_STYLE, textTransform: 'uppercase', maxWidth: '120px' }}
                  />
                </FormRow>

                <FormRow label="Client">
                  <select
                    value={form.clientId || ''}
                    onChange={e => setForm({ ...form, clientId: e.target.value })}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Client</option>
                    {activeClients.map(c => <option key={c.id} value={c.id}>{c.organisation || c.name}</option>)}
                  </select>
                </FormRow>

                <FormRow label="Category">
                  <select
                    value={form.category || ''}
                    onChange={e => {
                      const newType = e.target.value;
                      const autoColor = newType
                        ? autoPickProjectColor({ type: newType, projectId: Date.now(), projectName: form.projectName || '' })
                        : (form.color || '#3b82f6');
                      setForm({ ...form, category: newType, color: autoColor });
                    }}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Category</option>
                    {BRIO_PROJECT_TYPES.map(type => (
                      <option key={type} value={type.toLowerCase()}>{type}</option>
                    ))}
                  </select>
                </FormRow>

                <FormRow label="Color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={form.color || '#3b82f6'}
                      onChange={e => setForm({ ...form, color: e.target.value })}
                      style={{ width: '36px', height: '30px', border: '1px solid var(--brio-border, #d1d5db)', borderRadius: '6px', cursor: 'pointer', padding: '2px', flexShrink: 0 }}
                    />
                    <span style={{ display: 'inline-block', width: '48px', height: '30px', borderRadius: '6px', backgroundColor: form.color || '#3b82f6', border: '1px solid var(--brio-border, #d1d5db)', flexShrink: 0 }} />
                    {form.category && (
                      <span style={{ fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontWeight: 500 }}>
                        auto from category
                      </span>
                    )}
                  </div>
                </FormRow>

                {/* ── Location ──────────────────────────────────── */}
                <FormRow sectionHeader label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MapPin style={{ width: '14px', height: '14px' }} />
                    Location
                  </span>
                } />

                <FormRow label="Address" hint="Street address or coordinates — used for the map">
                  <input
                    type="text"
                    placeholder="e.g. 123 Marine Drive, Mumbai"
                    value={form.projectAddress || ''}
                    onChange={e => setForm({ ...form, projectAddress: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                {/* City and country are the readable location: they appear in
                    the comms project drawer and become the PROJECT-LOCATION
                    field of press-kit filenames (Alibaug-India). The address
                    above cannot serve that — it usually holds coordinates. */}
                <FormRow label="City" hint="Readable place name — used in press filenames">
                  <input
                    type="text"
                    placeholder="e.g. Alibaug"
                    value={form.city || ''}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Country">
                  <input
                    type="text"
                    placeholder="e.g. India"
                    value={form.country || ''}
                    onChange={e => setForm({ ...form, country: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                {(form.projectAddress || '').trim() && (
                  <FormRow label="Map">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.projectAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in Google Maps"
                      style={{ display: 'block', textDecoration: 'none' }}
                    >
                      <div style={{
                        width: '100%',
                        maxWidth: '340px',
                        height: '160px',
                        borderRadius: '8px',
                        border: '1px solid var(--brio-border, #d1d5db)',
                        overflow: 'hidden',
                        position: 'relative',
                        background: '#1a2e1a',
                      }}>
                        <iframe
                          title="Project location"
                          width="100%"
                          height="100%"
                          style={{ border: 0, pointerEvents: 'none' }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(form.projectAddress)}&t=k&z=16&ie=UTF8&iwloc=&output=embed`}
                        />
                        <div style={{
                          position: 'absolute', bottom: '6px', right: '8px',
                          background: 'rgba(0,0,0,0.65)', borderRadius: '4px',
                          padding: '2px 8px', fontSize: '10px', fontWeight: 600,
                          color: '#fff',
                          pointerEvents: 'none',
                        }}>
                          Open in Maps ↗
                        </div>
                      </div>
                    </a>
                  </FormRow>
                )}

                {/* ── Financials ────────────────────────────────── */}
                <FormRow sectionHeader label="Financials" />

                <FormRow label="Contract Currency" hint="Currency for all fees">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {[{ value: 'INR', label: '₹ INR' }, { value: 'EUR', label: '€ EUR' }].map(({ value, label }) => (
                      <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px' }}>
                        <input
                          type="radio"
                          name="editContractCurrency"
                          value={value}
                          checked={(form.contractCurrency || 'INR') === value}
                          onChange={() => setForm({ ...form, contractCurrency: value })}
                          style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="Total Design Fee" hint="Base fee (excl. CC retainer)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--brio-muted, #94a3b8)' }}>
                      {(form.contractCurrency || 'INR') === 'EUR' ? '€' : '₹'}
                    </span>
                    <input
                      type="number"
                      step="100"
                      placeholder="0"
                      value={form.budget || ''}
                      onChange={e => setForm({ ...form, budget: e.target.value })}
                      style={{ ...INPUT_STYLE, maxWidth: '180px' }}
                    />
                  </div>
                </FormRow>

                <FormRow label="Project Size" hint="Built-up area">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={form.projectSize || ''}
                      onChange={e => setForm({ ...form, projectSize: e.target.value })}
                      style={{ ...INPUT_STYLE, maxWidth: '160px' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--brio-muted, #94a3b8)' }}>m²</span>
                  </div>
                </FormRow>

                {/* ── Construction Budget ───────────────────────── */}
                <FormRow sectionHeader label="Construction Budget (optional)" />

                <FormRow label="Target Budget" hint="Initial estimate">
                  <input
                    type="number"
                    step="1000"
                    placeholder={currSymbol}
                    value={form.targetConstructionBudget || ''}
                    onChange={e => setForm({ ...form, targetConstructionBudget: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '200px' }}
                  />
                </FormRow>

                <FormRow label="Final Budget" hint="Actual cost">
                  <input
                    type="number"
                    step="1000"
                    placeholder={currSymbol}
                    value={form.finalConstructionBudget || ''}
                    onChange={e => setForm({ ...form, finalConstructionBudget: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '200px' }}
                  />
                </FormRow>

              </tbody>
            </BrioTable>
          </BrioByCode>
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════
          EDIT PROJECT MODAL — BRIO table 1b form layout
         ════════════════════════════════════════════════════════════ */}
      {modal === 'editProject' && (
        <BrioModal
          title="Edit Project"
          onClose={() => { setModal(null); setForm({}); setOriginalProjectSnapshot(null); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button
                onClick={() => { setModal(null); setForm({}); setOriginalProjectSnapshot(null); }}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleSaveProject} className="brio-btn brio-btn-primary">
                Update Project
              </button>
            </BrioActionGroup>
          }
        >
          <BrioByCode code={MODAL_TABLE_CODE}>
            <BrioTable {...modalTableProps}>
              <tbody>

                {/* ── Identity ──────────────────────────────────── */}
                <FormRow sectionHeader label="Identity" />

                <FormRow label="Name">
                  <input
                    type="text"
                    placeholder="Project name"
                    value={form.projectName || ''}
                    onChange={e => setForm({ ...form, projectName: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Code" hint="Max 4 chars">
                  <input
                    type="text"
                    placeholder="ACME"
                    maxLength="4"
                    value={form.projectCode || ''}
                    onChange={e => {
                      const newCode = e.target.value.toUpperCase();
                      const autoEmail = form.projectCode ? `${form.projectCode}@architecturebrio.com` : '';
                      const emailFollowsCode = !form.projectEmailId || form.projectEmailId === autoEmail;
                      setForm({
                        ...form,
                        projectCode: newCode,
                        projectEmailId: emailFollowsCode
                          ? (newCode ? `${newCode}@architecturebrio.com` : '')
                          : form.projectEmailId,
                      });
                    }}
                    style={{ ...INPUT_STYLE, textTransform: 'uppercase', maxWidth: '120px' }}
                  />
                </FormRow>

                <FormRow label="Invoice Project Name" hint="Optional. Used on invoices instead of the internal project name.">
                  <input
                    type="text"
                    placeholder="e.g. Residence Kumar — Phase 2"
                    value={form.invoiceProjectName || ''}
                    onChange={e => setForm({ ...form, invoiceProjectName: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Project Email ID" hint="Email identity used for project communications (configured on supervisor's mail client).">
                  <input
                    type="email"
                    placeholder="e.g. AR@architecturebrio.com"
                    value={form.projectEmailId || ''}
                    onChange={e => setForm({ ...form, projectEmailId: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Comms & Outreach" hint="Show this project on the Comms page for press kits, notes and outreach tracking.">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.commsEnabled || false}
                      onChange={e => setForm({ ...form, commsEnabled: e.target.checked })}
                    />
                    <span style={{ fontSize: '11px' }}>Include in Comms &amp; Outreach</span>
                  </label>
                </FormRow>

                <FormRow label="Client">
                  <select
                    value={form.clientId || ''}
                    onChange={e => setForm({ ...form, clientId: e.target.value })}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Client</option>
                    {activeClients.map(c => <option key={c.id} value={c.id}>{c.organisation || c.name}</option>)}
                  </select>
                </FormRow>

                <FormRow label="Type" hint="Used for filtering & color">
                  <select
                    value={form.category || ''}
                    onChange={e => {
                      const newType = e.target.value;
                      if (form.autoColorEnabled && newType) {
                        const autoColor = autoPickProjectColor({
                          type: newType,
                          projectId: form.editProjectId,
                          projectName: form.projectName
                        });
                        setForm({ ...form, category: newType, color: autoColor });
                      } else {
                        setForm({ ...form, category: newType });
                      }
                    }}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Type</option>
                    {BRIO_PROJECT_TYPES.map(type => (
                      <option key={type} value={type.toLowerCase()}>{type}</option>
                    ))}
                    {form.category && !BRIO_PROJECT_TYPES.map(t => t.toLowerCase()).includes(form.category.toLowerCase()) && (
                      <option value={form.category}>(Unknown: {form.category})</option>
                    )}
                  </select>
                </FormRow>

                {isAdmin && (
                  <FormRow label="Status">
                    <select
                      value={form.status || 'active'}
                      onChange={e => setForm({ ...form, status: e.target.value })}
                      style={SELECT_STYLE}
                    >
                      <option value="active">✅ Active</option>
                      <option value="paused">⏸️ Paused</option>
                      <option value="completed">✔️ Completed</option>
                      <option value="archived">📦 Archived</option>
                    </select>
                    <div style={HINT_STYLE}>Users see Active only · Supervisors see Active + Paused · Admins see all</div>
                  </FormRow>
                )}

                <FormRow label="Color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={form.color || '#3b82f6'}
                      onChange={e => setForm({ ...form, color: e.target.value, autoColorEnabled: false })}
                      style={{
                        width: '36px', height: '30px', border: '1px solid var(--brio-border, #d1d5db)',
                        borderRadius: '6px', cursor: form.autoColorEnabled ? 'not-allowed' : 'pointer',
                        opacity: form.autoColorEnabled ? 0.7 : 1, padding: '2px', flexShrink: 0,
                      }}
                    />
                    <span style={{
                      display: 'inline-block', width: '48px', height: '30px', borderRadius: '6px',
                      backgroundColor: form.color || '#3b82f6', border: '1px solid var(--brio-border, #d1d5db)',
                      flexShrink: 0,
                    }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={form.autoColorEnabled ?? true}
                        onChange={e => {
                          const autoEnabled = e.target.checked;
                          if (autoEnabled && form.category) {
                            const autoColor = autoPickProjectColor({
                              type: form.category,
                              projectId: form.editProjectId,
                              projectName: form.projectName
                            });
                            setForm({ ...form, autoColorEnabled: autoEnabled, color: autoColor });
                          } else {
                            setForm({ ...form, autoColorEnabled: autoEnabled });
                          }
                        }}
                        style={{ width: '14px', height: '14px' }}
                      />
                      <span style={{ color: form.autoColorEnabled ? 'var(--brio-gold, #9F902A)' : 'var(--brio-muted, #94a3b8)', fontWeight: form.autoColorEnabled ? 600 : 400 }}>
                        Auto from Type
                      </span>
                    </label>
                  </div>
                </FormRow>

                {/* ── Location ──────────────────────────────────── */}
                <FormRow sectionHeader label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MapPin style={{ width: '14px', height: '14px' }} />
                    Location
                  </span>
                } />

                <FormRow label="Address" hint="Street address or coordinates — used for the map">
                  <input
                    type="text"
                    placeholder="e.g. 123 Marine Drive, Mumbai"
                    value={form.projectAddress || ''}
                    onChange={e => setForm({ ...form, projectAddress: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                {/* City and country are the readable location: they appear in
                    the comms project drawer and become the PROJECT-LOCATION
                    field of press-kit filenames (Alibaug-India). The address
                    above cannot serve that — it usually holds coordinates. */}
                <FormRow label="City" hint="Readable place name — used in press filenames">
                  <input
                    type="text"
                    placeholder="e.g. Alibaug"
                    value={form.city || ''}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Country">
                  <input
                    type="text"
                    placeholder="e.g. India"
                    value={form.country || ''}
                    onChange={e => setForm({ ...form, country: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                {(form.projectAddress || '').trim() && (
                  <FormRow label="Map">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.projectAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in Google Maps"
                      style={{ display: 'block', textDecoration: 'none' }}
                    >
                      <div style={{
                        width: '100%',
                        maxWidth: '340px',
                        height: '160px',
                        borderRadius: '8px',
                        border: '1px solid var(--brio-border, #d1d5db)',
                        overflow: 'hidden',
                        position: 'relative',
                        background: '#1a2e1a',
                      }}>
                        <iframe
                          title="Project location"
                          width="100%"
                          height="100%"
                          style={{ border: 0, pointerEvents: 'none' }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(form.projectAddress)}&t=k&z=16&ie=UTF8&iwloc=&output=embed`}
                        />
                        <div style={{
                          position: 'absolute', bottom: '6px', right: '8px',
                          background: 'rgba(0,0,0,0.65)', borderRadius: '4px',
                          padding: '2px 8px', fontSize: '10px', fontWeight: 600,
                          color: '#fff',
                          pointerEvents: 'none',
                        }}>
                          Open in Maps ↗
                        </div>
                      </div>
                    </a>
                  </FormRow>
                )}

                <FormRow label="Plot Area" hint="Land / site area in m²">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 500"
                    value={form.plotArea ?? ''}
                    onChange={e => setForm({ ...form, plotArea: e.target.value === '' ? null : Number(e.target.value) })}
                    style={{ ...INPUT_STYLE, maxWidth: '180px' }}
                  />
                </FormRow>

                {/* ── Financials ────────────────────────────────── */}
                <FormRow sectionHeader label="Financials" />

                <FormRow label="Contract Currency" hint="Currency for all fees">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {[{ value: 'INR', label: '₹ INR' }, { value: 'EUR', label: '€ EUR' }].map(({ value, label }) => (
                      <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px' }}>
                        <input
                          type="radio"
                          name="createContractCurrency"
                          value={value}
                          checked={(form.contractCurrency || 'INR') === value}
                          onChange={() => setForm({ ...form, contractCurrency: value })}
                          style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="Base Design Fee" hint="Excl. CC retainer">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--brio-muted, #94a3b8)' }}>
                      {(form.contractCurrency || 'INR') === 'EUR' ? '€' : '₹'}
                    </span>
                    <input
                      type="number"
                      step="100"
                      placeholder="0"
                      value={form.budget || ''}
                      onChange={e => setForm({ ...form, budget: e.target.value })}
                      style={{ ...INPUT_STYLE, maxWidth: '180px' }}
                    />
                  </div>
                </FormRow>

                <FormRow label="Project Size" hint="Built-up area">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={form.projectSize || ''}
                      onChange={e => setForm({ ...form, projectSize: e.target.value })}
                      style={{ ...INPUT_STYLE, maxWidth: '160px' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--brio-muted, #94a3b8)' }}>m²</span>
                  </div>
                </FormRow>

                {/* ── CC Retainer ───────────────────────────────── */}
                <FormRow sectionHeader label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Briefcase style={{ width: '14px', height: '14px' }} />
                    CC Retainer
                  </span>
                } />

                <FormRow label="Monthly Amount">
                  <input
                    type="number"
                    step="100"
                    placeholder={currSymbol}
                    value={form.retainerMonthlyAmount || 0}
                    onChange={e => setForm({ ...form, retainerMonthlyAmount: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '200px' }}
                  />
                </FormRow>

                <FormRow label="Total Months">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={form.retainerTotalMonths || 12}
                    onChange={e => setForm({ ...form, retainerTotalMonths: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '100px' }}
                  />
                  <div style={HINT_STYLE}>To start/pause/stop CC — use the Timeline page</div>
                </FormRow>

                {/* ── Misc Design Fees (additional scopes) ────── */}
                <FormRow sectionHeader label="Additional Scopes / Misc Design Fees" />
                <FormRow label="Scopes" hint="Ad-hoc invoicing scopes outside base fee & CC">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(form.miscFeeScopes || []).map((scope, idx) => (
                      <div key={scope.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="text"
                          placeholder="Scope name"
                          value={scope.name || ''}
                          onChange={e => {
                            const updated = [...form.miscFeeScopes];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setForm({ ...form, miscFeeScopes: updated });
                          }}
                          style={{ ...INPUT_STYLE, flex: 1, minWidth: '120px' }}
                        />
                        <input
                          type="number"
                          step="1000"
                          min="0"
                          placeholder={currSymbol}
                          value={scope.amount || ''}
                          onChange={e => {
                            const updated = [...form.miscFeeScopes];
                            updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                            setForm({ ...form, miscFeeScopes: updated });
                          }}
                          style={{ ...INPUT_STYLE, maxWidth: '140px' }}
                        />
                        <button
                          onClick={() => {
                            const updated = form.miscFeeScopes.filter((_, i) => i !== idx);
                            setForm({ ...form, miscFeeScopes: updated });
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brio-red, #dc2626)', fontSize: '14px', padding: '2px 4px', fontFamily: 'var(--brio-font-family)' }}
                          title="Remove scope"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '6px', alignSelf: 'flex-start' }}>
                      <button
                        onClick={() => {
                          const newScope = { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7), name: '', amount: 0 };
                          setForm({ ...form, miscFeeScopes: [...(form.miscFeeScopes || []), newScope] });
                        }}
                        style={{ background: 'none', border: '1px dashed var(--brio-border)', cursor: 'pointer', padding: '4px 10px', fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontFamily: 'var(--brio-font-family)' }}
                      >
                        + Add scope
                      </button>
                      <button
                        onClick={() => { setShowMiscImport(!showMiscImport); setMiscImportText(''); }}
                        style={{ background: 'none', border: '1px dashed var(--brio-border)', cursor: 'pointer', padding: '4px 10px', fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontFamily: 'var(--brio-font-family)' }}
                      >
                        {showMiscImport ? 'Cancel import' : 'Bulk import'}
                      </button>
                    </div>
                    {showMiscImport && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                        <textarea
                          value={miscImportText}
                          onChange={e => setMiscImportText(e.target.value)}
                          placeholder={'Paste scope names & amounts (tab or comma separated):\nInterior Design, 500000\nLandscaping\t250000'}
                          rows={4}
                          style={{ ...INPUT_STYLE, resize: 'vertical', fontSize: '11px', lineHeight: '1.5' }}
                        />
                        <button
                          onClick={() => {
                            const lines = miscImportText.trim().split('\n').filter(l => l.trim());
                            if (!lines.length) return;
                            const parsed = lines.map(line => {
                              const sep = line.includes('\t') ? '\t' : ',';
                              const cols = line.split(sep).map(c => c.trim());
                              const name = cols[0] || '';
                              const amount = parseFloat(cols[1]) || 0;
                              return { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7), name, amount };
                            }).filter(s => s.name);
                            if (parsed.length > 0) {
                              setForm({ ...form, miscFeeScopes: [...(form.miscFeeScopes || []), ...parsed] });
                              setMiscImportText('');
                              setShowMiscImport(false);
                            }
                          }}
                          disabled={!miscImportText.trim()}
                          style={{ background: 'none', border: '1px solid var(--brio-gold, #9F902A)', cursor: miscImportText.trim() ? 'pointer' : 'default', padding: '4px 12px', fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontFamily: 'var(--brio-font-family)', alignSelf: 'flex-start', opacity: miscImportText.trim() ? 1 : 0.5 }}
                        >
                          Import {miscImportText.trim().split('\n').filter(l => l.trim()).length} scope(s)
                        </button>
                      </div>
                    )}
                  </div>
                </FormRow>

                {isAdmin && (
                  <FormRow label="Contracting entity" hint="Who holds the contract. Partner hours on an ABVOF project stay out of the VOF statement.">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[{ value: 'SBPL', label: 'Studio BRIO Pvt Ltd (SBPL)' }, { value: 'ABVOF', label: 'Architecture BRIO V.O.F. (ABVOF)' }].map(({ value, label }) => (
                        <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px' }}>
                          <input
                            type="radio"
                            name="editContractingEntity"
                            value={value}
                            checked={(form.contractingEntity || 'SBPL') === value}
                            onChange={() => setForm({ ...form, contractingEntity: value })}
                            style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </FormRow>
                )}

                <FormRow label="Total Design Fee">
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: '8px',
                    padding: '6px 0', fontWeight: 600, fontSize: '15px',
                    color: 'var(--brio-gold, #9F902A)',
                  }}>
                    <span>{fmtCurr(totalDesignFee, form.contractCurrency || 'INR')}</span>
                    {(ccRetainer > 0 || miscFeeTotal > 0) && (
                      <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--brio-muted, #94a3b8)' }}>
                        ({fmtCurr(baseFee, form.contractCurrency || 'INR')}{ccRetainer > 0 ? ` + CC ${fmtCurr(ccRetainer, form.contractCurrency || 'INR')}` : ''}{miscFeeTotal > 0 ? ` + Misc ${fmtCurr(miscFeeTotal, form.contractCurrency || 'INR')}` : ''})
                      </span>
                    )}
                  </div>
                </FormRow>

                {/* ── Construction Budget ───────────────────────── */}
                <FormRow sectionHeader label="Construction Budget" />

                <FormRow label="Target Budget" hint="Initial estimate">
                  <input
                    type="number"
                    step="1000"
                    placeholder={currSymbol}
                    value={form.targetConstructionBudget || ''}
                    onChange={e => setForm({ ...form, targetConstructionBudget: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '200px' }}
                  />
                </FormRow>

                <FormRow label="Final Budget" hint="Actual cost">
                  <input
                    type="number"
                    step="1000"
                    placeholder={currSymbol}
                    value={form.finalConstructionBudget || ''}
                    onChange={e => setForm({ ...form, finalConstructionBudget: e.target.value })}
                    style={{ ...INPUT_STYLE, maxWidth: '200px' }}
                  />
                </FormRow>

                {targetCB > 0 && finalCB > 0 && (
                  <FormRow label="Variance">
                    <span style={{
                      fontSize: '14px', fontWeight: 600, padding: '6px 0', display: 'inline-block',
                      color: cbVariance > 0 ? '#dc2626' : '#16a34a',
                    }}>
                      {cbVariance >= 0 ? '+' : ''}{fmtCurr(cbVariance, form.contractCurrency || 'INR')} ({cbVariance >= 0 ? '+' : ''}{cbPercent}%)
                    </span>
                  </FormRow>
                )}

                {/* ── Supervisors (admin only) ─────────────────── */}
                {isAdmin && (
                  <>
                    <FormRow sectionHeader label="Supervisors" />
                    <FormRow label="Assign" hint="Manage this project">
                      <div style={{
                        display: 'flex', flexDirection: 'column', gap: '2px',
                        border: '1px solid var(--brio-border, #e2e8f0)',
                        borderRadius: '6px', padding: '4px',
                        maxHeight: '160px', overflowY: 'auto',
                      }}>
                        {activeUsers.filter(u => u.role === 'supervisor' || u.role === 'admin' || u.role === 'admin-pa').map(u => (
                          <label key={u.id} style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '4px 8px', borderRadius: '4px', cursor: 'pointer',
                            fontSize: '13px',
                          }}
                          className="brio-row--hoverable"
                          >
                            <input
                              type="checkbox"
                              checked={(form.supervisors || []).includes(u.id)}
                              onChange={e => {
                                const current = form.supervisors || [];
                                setForm({
                                  ...form,
                                  supervisors: e.target.checked
                                    ? [...current, u.id]
                                    : current.filter(id => id !== u.id)
                                });
                              }}
                              style={{ width: '14px', height: '14px', flexShrink: 0 }}
                            />
                            <div>
                              <div style={{ fontWeight: 500 }}>{u.name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)' }}>{u.email} · {u.role}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </FormRow>
                  </>
                )}

              </tbody>
            </BrioTable>
          </BrioByCode>
        </BrioModal>
      )}
    </div>
  );
}